import { yupResolver } from "@hookform/resolvers/yup";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetClientHierarchyById,
  useSearchClientListMutation,
  useSearchClientSavedFiltersMutation,
} from "../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../api/services/client/models";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import {
  InformationModal,
  MuiMenuList,
  RowProps,
  STATUS_BADGE_TYPE,
} from "../../components";
import { CommonListModal } from "../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../components/searchFilters/saveFilter/SaveFilter";
import {
  BE_ERROR_CODES,
  MAX_SAVE_FILTERS_LIMIT,
  PAGE_IDS,
  PAGE_URLS,
} from "../../helpers/constants";
import {
  getParsedBEErrors,
  isAffiliateClient,
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../helpers/utils";
import { useEnterKeyAction } from "../../hooks/useEnterKeyAction";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../redux/slices/client/createClientSelector";
import { ClientSearchWrapper } from "./ClientSearch.style";
import { ClientSearchForm } from "./components/clientSearchForm/ClientSearchForm";
import { ClientSearchHeader } from "./components/clientSearchHeader/ClientSearchHeader";
import { ClientSearchResult } from "./components/clientSearchResult/ClientSearchResult";
import ClientSearchResultHierarchy from "./components/ClientSearchResultHierarchy/ClientSearchResultHierarchy";
import {
  ClientResultRowValue,
  ClientSearchFormKey,
  ClientSearchFormProps,
  FilterProps,
} from "./modelsAndSchemas/Models";
import { ClientSearchValidationSchema } from "./modelsAndSchemas/ValidationSchema";
import { clientSearchSiteCoreKeys } from "./siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.SEARCH_CLIENT;
interface ClientSearchProps {
  showAsModal?: boolean;
  onRowSelected?: (row: RowProps) => void;
}

const ClientSearch: FC<ClientSearchProps> = ({
  showAsModal = false,
  onRowSelected,
}) => {
  const defaultValues = {
    [ClientSearchFormKey.companyName]: "",
    [ClientSearchFormKey.companyNameFilter]: clientSearchSiteCoreKeys.contains,
    [ClientSearchFormKey.clientCode]: "",
    [ClientSearchFormKey.clientCodeFilter]: clientSearchSiteCoreKeys.contains,
    [ClientSearchFormKey.clientType]: "",
    [ClientSearchFormKey.country]: "",
    [ClientSearchFormKey.city]: "",
    [ClientSearchFormKey.cityFilter]: clientSearchSiteCoreKeys.contains,
    [ClientSearchFormKey.status]: "",
  };

  const { isMobile } = useMediaQueries();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const [showModal, setShowModal] = useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [isOpenSaveFilterModal, setIsOpenSaveFilterModal] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterProps[]>([]);
  const [showSavedFilter, setShowSavedFilter] = useState(false);
  const [filterToDeleteClient, setFilterToDeleteClient] =
    useState<MenuListParams>({
      id: "",
      name: "",
    });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [preloadFilter, setPreloadFilter] = useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [preloadFilterValuesClient, setPreloadFilterValuesClient] = useState({
    id: "",
    name: "",
    filters: filtersData,
  });

  const [showHierarchyModal, setShowHierarchyModal] = useState<boolean>(false);
  const [hierarchyClientName, setHierarchyClientName] = useState<string>("");

  const clientData = useSelector(createClientSelector);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { fetchValidationMessage, commonLabels, commonValidationMessages } =
    useSiteCore();
  const [searchResultList, setSearchResultList] = useState<
    ClientResultRowValue[]
  >([]);
  const {
    mutateAsync: getClientList,
    isPending,
    isError: isSearchClientError,
    data: clientSearchResult,
    error: searchClientError,
  } = useSearchClientListMutation();

  const {
    data: listOfFilters,
    refetch: refetchListOfFilters,
    isError: isListOfFiltersError,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "ClientSearch",
  });

  const {
    mutateAsync: getClientHierarchyData,
    isPending: isClientHierarchyPending,
    isError: isClientHierarchyError,
    data: clientHierarchyResult,
  } = useGetClientHierarchyById();

  useEffect(() => {
    if (isNotEmpty(clientSearchResult)) {
      setSearchResultList(
        (clientSearchResult?.data as ClientResultRowValue[]) || [],
      );
    }
  }, [clientSearchResult]);

  useEffect(() => {
    if (isSearchClientError) {
      dispatch(toggleLoader({ isVisible: false }));
      const error = getParsedBEErrors(searchClientError);
      if (
        isNotEmpty(error) &&
        error[0].errorCode === BE_ERROR_CODES.SEARCH_CLIENT_LIMIT_EXCEEDED
      ) {
        setShowErrorMessage(true);
        setIsSearchClick(false);
      }
    }
  }, [searchClientError, isSearchClientError]);

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isClientHierarchyPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isClientHierarchyPending]);

  useEffect(() => {
    if (isClientHierarchyError || isListOfFiltersError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isClientHierarchyError, isListOfFiltersError]);

  const { control, getValues, reset, setValue, resetField, handleSubmit } =
    useForm({
      mode: "all",
      defaultValues: defaultValues,
      resolver: yupResolver(
        ClientSearchValidationSchema(
          fetchValidationMessage,
          commonValidationMessages,
        ),
      ),
    });

  useEffect(() => {
    if (showAsModal && clientData.clientTypeId) {
      setValue(ClientSearchFormKey.clientType, clientData.clientTypeId);
      setValue(ClientSearchFormKey.status, STATUS_BADGE_TYPE.active);
    } else {
      resetField(ClientSearchFormKey.clientType);
      resetField(ClientSearchFormKey.status);
    }
  }, [showAsModal]);

  const formValues = useWatch({ control });

  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    ClientSearchFormKey.cityFilter,
    ClientSearchFormKey.clientCodeFilter,
    ClientSearchFormKey.companyNameFilter,
    ClientSearchFormKey.clientType,
  ] as string[];

  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm = Object.entries(formValues).some(
    ([key, value]) => !ignoredFields.includes(key) && value.trim() !== "",
  );

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    getClientList(prepareData());
  };

  const getClientTypeId = () => {
    const { clientType } = getValues();
    if (!showAsModal) {
      return isNotEmpty(clientType) ? clientType : null;
    }

    return isAffiliateClient(clientData.clientTypeId) && isNotEmpty(clientType)
      ? clientType
      : clientData.clientTypeId;
  };

  const prepareData = () => {
    const {
      companyName,
      companyNameFilter,
      clientCode,
      clientCodeFilter,
      city,
      cityFilter,
      country,
      status,
    } = getValues();

    return {
      companyname: trimValue(companyName),
      companyfilter: companyNameFilter,
      clientcode: trimValue(clientCode),
      clientfilter: clientCodeFilter,
      city: trimValue(city),
      cityfilter: cityFilter,
      addresscountry: trimValue(country),
      status: trimValue(status),
      clientTypeId: getClientTypeId(),
    };
  };

  const handleClearAllSearch = () => {
    reset({
      ...defaultValues,
      clientType: showAsModal ? getValues().clientType : "",
    });
    removeSavedFilters();
    setIsSearchClick(false);
    setSearchResultList([]);
  };

  const handleSetFilterModal = () => {
    setIsOpenSaveFilterModal(!isOpenSaveFilterModal);
  };

  const handleClose = () => {
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleFilterRowClickClient = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadFilter(true);
    const itemFilterClientSearch = item as SaveSearchFilterParams;
    setPreloadFilterValuesClient({
      id: itemFilterClientSearch.id ?? "",
      name: itemFilterClientSearch.name ?? "",
      filters: mapFiltersToFormValues(
        itemFilterClientSearch.filters,
        defaultValues,
        ClientSearchFormKey,
      ),
    });
    reset(
      mapFiltersToFormValues(
        itemFilterClientSearch.filters,
        defaultValues,
        ClientSearchFormKey,
      ),
    );
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const removeSavedFilters = () => {
    setPreloadFilterValuesClient({ id: "", name: "", filters: [] });
    setPreloadFilter(false);
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: MenuListParams,
  ) => {
    event.stopPropagation();
    // if the selectd filter is deleted, remove the saved filter details from the memory
    if (preloadFilterValuesClient.name === item.name) {
      removeSavedFilters();
    }

    setFilterToDeleteClient({ id: item?.id, name: item?.name });
    if (showModal === false) {
      setShowModal(true);
    }
  };

  const handleModalClose = () => {
    if (showModal) {
      setShowModal(false);
    }
  };

  const handleMultipleDeleteModalClose = () => {
    if (showMultipleDeleteModal) {
      setShowMultipleDeleteModal(false);
    }
  };

  const handlePrimaryButtonClick = () => {
    setShowModal(false);
  };

  const handleMultipleDeletePrimaryButtonClick = () => {
    setShowMultipleDeleteModal(false);
  };

  const handleSavedFilter = () => {
    if (showSavedFilter === false) {
      setShowSavedFilter(true);
    }
  };

  const closeSaveFilterModal = () => {
    setIsOpenSaveFilterModal(false);
  };

  const handleSaveFilterModal = () => {
    if (
      listOfFilters &&
      listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadFilter
    ) {
      if (showMultipleDeleteModal === false) {
        setShowMultipleDeleteModal(true);
      }
    } else {
      handleSetFilterModal();
    }

    const values = getValues();

    const filters: {
      fieldName: keyof ClientSearchFormProps;
      operatorIdKey?: keyof ClientSearchFormProps;
    }[] = [
      {
        fieldName: ClientSearchFormKey.companyName,
        operatorIdKey: ClientSearchFormKey.companyNameFilter,
      },
      {
        fieldName: ClientSearchFormKey.clientCode,
        operatorIdKey: ClientSearchFormKey.clientCodeFilter,
      },
      {
        fieldName: ClientSearchFormKey.clientType,
        operatorIdKey: ClientSearchFormKey.clientType,
      },
      { fieldName: ClientSearchFormKey.country },
      {
        fieldName: ClientSearchFormKey.city,
        operatorIdKey: ClientSearchFormKey.cityFilter,
      },
      { fieldName: ClientSearchFormKey.status },
    ];

    const data: FilterProps[] = filters
      .filter(({ fieldName }) => isNotEmpty(values[fieldName].trim()))
      .map(({ fieldName, operatorIdKey }) => ({
        fieldName: fieldName as string,
        fieldValue: trimValue(values[fieldName]),
        operatorId: operatorIdKey ? values[operatorIdKey] : "",
        savedSearchFilterDataType: 0,
      }));

    if (preloadFilterValuesClient?.id) {
      setPreloadFilterValuesClient({
        id: preloadFilterValuesClient.id,
        name: preloadFilterValuesClient.name,
        filters: data,
      });
    } else {
      setFiltersData(data);
    }
  };

  const onSaveAsNewFilterClick = () => {
    setFiltersData([...preloadFilterValuesClient.filters]);
    if (listOfFilters && listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT) {
      setShowMultipleDeleteModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadFilter(false);
    }
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadFilter(false);
    }
    setIsOpenSaveFilterModal(true);
  };

  const handleEditClient = (clientId: string) => {
    navigate(PAGE_URLS.EDIT_CLIENT, {
      state: { clientId: clientId, from: PAGE_URLS.SEARCH_CLIENT },
    });
  };
  const handleViewClient = (clientId: string) => {
    navigate(`${PAGE_URLS.VIEW_CLIENT}/${clientId}`, {
      state: { clientId: clientId, from: PAGE_URLS.SEARCH_CLIENT },
    });
  };

  const handleHierarchyClick = (clientId: string) => {
    getClientHierarchyData(clientId);

    const clientName = clientSearchResult?.data?.find(
      (cli: ClientResultRowValue) => cli.id === clientId,
    )?.name;

    setHierarchyClientName(clientName ?? "");

    setShowHierarchyModal(true);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(handleSearchClick),
  });

  return (
    <ClientSearchWrapper data-testid="client-search">
      {!showAsModal && <ClientSearchHeader />}

      <ClientSearchForm
        control={control}
        isValidForm={isValidForm}
        handleSearchClick={handleSubmit(handleSearchClick)}
        handleClearAllSearch={handleClearAllSearch}
        handleSaveFilter={handleSaveFilterModal}
        showAsModal={showAsModal}
        handleSavedFilter={handleSavedFilter}
        refetchListOfFilters={refetchListOfFilters}
        anchorRef={anchorRef}
        disableActionBtn={!(listOfFilters && listOfFilters?.length)}
      />
      <ClientSearchResult
        rows={searchResultList}
        isSearch={isSearchClick}
        showAsModal={showAsModal}
        onRowSelected={onRowSelected}
        handleHierarchyClick={handleHierarchyClick}
        onEditClick={handleEditClient}
        handleViewClientClick={handleViewClient}
      />
      {isOpenSaveFilterModal && !preloadFilter && (
        <SaveFilterModal
          showModal={isOpenSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={0}
          filters={filtersData}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {preloadFilter && isOpenSaveFilterModal && (
        <OverwriteFilterModal
          showModal={isOpenSaveFilterModal && preloadFilter}
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClick}
          overrideFilterValues={preloadFilterValuesClient}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {showSavedFilter &&
        isNotEmpty(listOfFilters) &&
        (!isMobile ? (
          <MuiMenuList
            menuArray={listOfFilters}
            refValue={anchorRef.current}
            open={showSavedFilter}
            onClose={handleClose}
            onRowClick={handleFilterRowClickClient}
            onActionBtnClick={handleDeleteClick}
          />
        ) : (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={showSavedFilter}
            onIconButtonClick={handleDeleteClick}
            onFilterClick={handleFilterRowClickClient}
            buttonIcon={<DeleteIcon />}
            listItems={listOfFilters}
            closeHandler={handleClose}
            secondaryButtonTitle={commonLabels.close}
          />
        ))}

      {showModal && (
        <DeleteFilterModal
          showModal={showModal}
          handleModalClose={handleModalClose}
          handlePrimaryButtonClick={handlePrimaryButtonClick}
          filterToDelete={filterToDeleteClient}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {showMultipleDeleteModal && (
        <DeleteMultipleFilterModal
          showModal={showMultipleDeleteModal}
          handleModalClose={handleMultipleDeleteModalClose}
          handlePrimaryButtonClick={handleMultipleDeletePrimaryButtonClick}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
          callbackAction={callbackAction}
        />
      )}

      {showHierarchyModal && (
        <ClientSearchResultHierarchy
          open={showHierarchyModal}
          onClose={() => setShowHierarchyModal(false)}
          hierarchyData={clientHierarchyResult?.data}
          clientName={hierarchyClientName}
        />
      )}

      {showErrorMessage && (
        <InformationModal
          showModal={showErrorMessage}
          handleModalClose={handleCloseErrorMessage}
          handlePrimaryButtonClick={handleCloseErrorMessage}
          message={commonLabels.refineYourSearchInformationMessage}
        />
      )}
    </ClientSearchWrapper>
  );
};

export default ClientSearch;
